import { filter, groupBy, sortBy, toPairs } from 'lodash-es';
import { type MouseEvent, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { GroupAdminListItem } from '@/components/groups/group-admin-sectioned-list/group-admin-list-item';
import useChangeGroupMemberRole from '@/hooks/groups/use-change-group-member-role';
import useGroupInfo from '@/hooks/groups/use-group-info';
import { useGroupMembersWithRemarkName } from '@/hooks/groups/use-group-members';
import useTransferGroup from '@/hooks/groups/use-transfer-group';
import { useIsDesktop } from '@/hooks/use-user-platform';
import { type GroupMember, Role } from '@/models/group/group-info';
import { useUserId } from '@/stores/auth';
import { makePrompt } from '@/stores/prompter';
import cn from '@/utils/merge-class-names';

interface Props {
  className?: string;
  groupId: number;
}

export default function GroupAdminList({ className, groupId }: Props) {
  const userId = useUserId();
  const { t } = useTranslation();
  const isDesktop = useIsDesktop();
  const ref = useRef<HTMLUListElement>(null);
  const { members } = useGroupMembersWithRemarkName(groupId);
  const { isGroupOwner } = useGroupInfo(groupId);
  const { mutateAsync: changeGroupMemberRole } = useChangeGroupMemberRole(groupId);
  const { mutateAsync: transferGroup } = useTransferGroup(groupId);

  const groupedMembers = useMemo(() => {
    const groupedPairs = toPairs(
      groupBy(
        filter(members, (m) => m.Role !== Role.Member),
        (m) => m.Role,
      ),
    );
    return sortBy(groupedPairs, ([role]) => -Number(role));
  }, [members]);

  const handleChangeRole = (member: GroupMember, event?: MouseEvent) => {
    let position: [number, number] | undefined;
    if (isDesktop && event && ref.current) {
      position = [event.clientX, event.clientY];
    }

    return makePrompt({
      key: 'change-admin-role',
      type: 'action-sheet',
      title: '',
      meta: position ? { position } : undefined,
      resolutionButtons:
        member.Role === 10
          ? [
              {
                resolution: 'operate-owner',
                label: t('conversation.setting.group.admin.operate-owner'),
                type: 'informative',
              },
            ]
          : userId === member.UserID
            ? [
                {
                  resolution: 'operate-self',
                  label: t('conversation.setting.group.admin.operate-self'),
                  type: 'informative',
                },
              ]
            : [
                {
                  resolution: 'to-member',
                  label: t('conversation.setting.group.admin.to-member'),
                  hidden: member.Role !== 9,
                },
                {
                  resolution: 'to-admin',
                  label: t('conversation.setting.group.admin.to-admin'),
                  hidden: member.Role >= 9,
                },
                {
                  resolution: 'transfer-owner',
                  label: t('conversation.setting.group.admin.transfer-owner'),
                  hidden: member.Role < 9 || !isGroupOwner,
                },
              ],
      promise: async (resolution) => {
        if (typeof resolution === 'string' && resolution) {
          switch (resolution) {
            case 'to-admin':
              await changeGroupMemberRole({ userId: member.UserID, role: 9 });
              break;
            case 'to-member':
              await changeGroupMemberRole({ userId: member.UserID, role: 0 });
              break;
            case 'transfer-owner':
              if (isGroupOwner) await transferGroup(member.UserID);
              break;
          }
        }
      },
    });
  };

  return (
    <ul
      ref={ref}
      className={cn('mx-5 flex flex-col gap-5', className)}>
      {groupedMembers.map(([key, members]) => (
        <li
          key={key}
          className='first:border-b first:pb-5 only:border-none'>
          <ul className='flex flex-col items-stretch gap-4'>
            {members?.map((m) => (
              <li
                key={m.ID}
                className='flex items-center gap-2'>
                <GroupAdminListItem
                  showBadge
                  member={m}
                  canManage
                  onClick={(e) => handleChangeRole(m, e)}
                />
              </li>
            ))}
          </ul>
        </li>
      ))}
    </ul>
  );
}
